import React from 'react';
import { MathJaxContext, MathJax } from 'better-react-mathjax';

interface MathJaxRendererProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * MathJax Renderer Component
 * Simplified version for better compatibility in web app
 */

// Simplified MathJax configuration
const mathJaxConfig = {
  loader: { load: ['input/tex', 'output/chtml'] },
  tex: {
    inlineMath: [
      ['$', '$'],
      ['\\(', '\\)']
    ],
    displayMath: [
      ['$$', '$$'],
      ['\\[', '\\]']
    ],
    processEscapes: true,
    processEnvironments: true
  },
  options: {
    skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre']
  }
};

const MathJaxRenderer: React.FC<MathJaxRendererProps> = ({ children, className }) => {
  // Simple preprocessing for common issues
  const preprocessText = (text: string): string => {
    if (!text || typeof text !== 'string') {
      return text;
    }

    let processedText = text;

    // Handle newline escape sequences
    processedText = processedText.replace(/\\n/g, '\n');

    // Handle double backslashes first
    processedText = processedText.replace(/\\\\([a-zA-Z]+)/g, '\\$1');

    // Auto-wrap common patterns if no math delimiters exist
    if (!text.includes('$') && !text.includes('\\(') && !text.includes('\\[')) {
      
      // Check for multiple choice chemistry questions with options (A), (B), (C), (D)
      if (processedText.includes('(A)') && processedText.includes('(B)') && 
          (processedText.includes('\\Delta H') || processedText.includes('\\leftrightarrow') || processedText.includes('_'))) {
        // Split by options and process each line separately
        const lines = processedText.split(/\n+/);
        const processedLines = lines.map(line => {
          if (line.trim().match(/^\([A-D]\)/)) {
            return line; // Keep option lines as is
          }
          
          // Process the main question text
          let processedLine = line;
          
          // Chemical equations and reactions
          processedLine = processedLine.replace(/\\Delta H/g, '$\\Delta H$');
          processedLine = processedLine.replace(/\\leftrightarrow/g, '$\\leftrightarrow$');
          processedLine = processedLine.replace(/([A-Z][a-z]?)_(\d+)/g, '$1_{$2}$');
          
          return processedLine;
        });
        processedText = processedLines.join('\n');
      } else {
        // Standard math expressions auto-wrapping
        // Powers and subscripts
        processedText = processedText.replace(/([a-zA-Z0-9])\^([a-zA-Z0-9]+)/g, '$1^{$2}$');
        processedText = processedText.replace(/([a-zA-Z0-9])_([a-zA-Z0-9]+)/g, '$1_{$2}$');
        
        // Times symbol
        processedText = processedText.replace(/\s*×\s*/g, ' $\\times$ ');
        
        // Greek letters
        processedText = processedText.replace(/\\(alpha|beta|gamma|delta|epsilon|zeta|eta|theta|iota|kappa|lambda|mu|nu|xi|omicron|pi|rho|sigma|tau|upsilon|phi|chi|psi|omega)/g, '$\\$1$');
        
        // Fractions (only if not already wrapped)
        processedText = processedText.replace(/\\frac\{[^}]+\}\{[^}]+\}/g, '$$&$$');
        
        // Arrows
        processedText = processedText.replace(/\\(rightarrow|leftarrow|leftrightarrow|rightleftharpoons)/g, '$\\$1$');
        
        // Text commands in LaTeX (\text{...})
        processedText = processedText.replace(/\\text\{[^}]+\}/g, '$$&$$');
      }
    }

    return processedText;
  };

  const renderContent = () => {
    if (typeof children === 'string') {
      const processedText = preprocessText(children);
      return processedText;
    }
    return children;
  };

  return (
    <span className={className}>
      <MathJax>
        {renderContent()}
      </MathJax>
    </span>
  );
};

/**
 * MathJax Provider Component
 * Wrap your app or component tree with this to enable MathJax rendering
 */
export const MathJaxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <MathJaxContext config={mathJaxConfig}>
      {children}
    </MathJaxContext>
  );
};

export default MathJaxRenderer;